import { useEffect, useState } from "react";
import { useVault } from "../vault/store";
import { useOffline } from "../vault/offline";
import { useAuth } from "../auth/store";
import { ApiError, probeReachable } from "../api/client";
import { isTauri, offlineStatus, localSettingsGet, type OfflineStatus } from "../desktop/bridge";
import { useT } from "../i18n";
import { AppShell } from "./AppShell";
import { MIN_MASTER_LEN } from "../vault/policy";
import { Shield } from "lucide-react";

export function VaultGate() {
  const { phase, refresh, enterOfflineLocked } = useVault();
  const clearAuth = useAuth((s) => s.clear);
  const t = useT();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [canOffline, setCanOffline] = useState(false);

  useEffect(() => {
    // App may have already routed us into offline mode (no session token after restart); don't
    // re-bootstrap over it. Read via getState so this isn't a hook dependency.
    const cur = useVault.getState().phase;
    if (cur === "offline-locked" || cur === "offline-unlocked") return;
    let alive = true;
    const handleErr = (e: unknown) => {
      if (!alive) return;
      // An invalid/expired session → back to login. A mere connectivity drop (ApiError status 0)
      // or other error → offer offline if a usable cache exists, else a retry banner.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        clearAuth();
      } else {
        setLoadError("Cannot reach the server. Check your connection and retry.");
      }
    };
    (async () => {
      // Web (or no desktop): unchanged online flow.
      if (!isTauri()) {
        refresh().catch(handleErr);
        return;
      }
      // Desktop: note whether a usable offline cache exists (for the "Continue offline" affordance).
      const st: OfflineStatus = await offlineStatus().catch(() => ({ exists: false }));
      if (alive && st.exists && !st.expired) setCanOffline(true);
      // Online-first: probe reachability, then fall back to offline if down + cache usable.
      const reachable = await probeReachable();
      if (!alive) return;
      if (reachable) {
        refresh().catch(handleErr);
      } else if (st.exists && !st.expired) {
        enterOfflineLocked();
      } else {
        setLoadError("Cannot reach the server. Check your connection and retry.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [refresh, clearAuth, enterOfflineLocked]);

  if (phase === "unlocked") return <AppShell />;
  if (phase === "offline-unlocked") return <AppShell />;
  if (phase === "offline-locked") return <OfflineUnlockCard />;
  if (phase === "enroll") return <Card title={t("Set your master password")}><EnrollForm /></Card>;
  if (phase === "locked") return <Card title={t("Unlock your vault")}><UnlockForm /></Card>;
  if (loadError)
    return (
      <Centered>
        <div className="flex flex-col items-center gap-3">
          <span className="text-danger">{loadError}</span>
          <button onClick={() => location.reload()} className="rounded bg-accent px-3 py-1.5 text-white hover:bg-accent2">
            {t("Retry")}
          </button>
          {canOffline && (
            <button onClick={enterOfflineLocked} className="rounded border border-border px-3 py-1.5 hover:border-accent">
              {t("Continue offline")}
            </button>
          )}
          <button onClick={clearAuth} className="text-xs text-muted hover:text-fg">
            {t("Sign out")}
          </button>
        </div>
      </Centered>
    );
  return <Centered>{t("Loading")}…</Centered>;
}

function OfflineUnlockCard() {
  const t = useT();
  const offlineEnter = useVault((s) => s.offlineEnter);
  const error = useOffline((s) => s.error);
  const terminal = useOffline((s) => s.terminal);
  const clearAuth = useAuth((s) => s.clear);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [builtAt, setBuiltAt] = useState<number | undefined>(undefined);
  const [hint, setHint] = useState("");

  useEffect(() => {
    offlineStatus().then((s) => setBuiltAt(s.built_at)).catch(() => undefined);
    localSettingsGet().then((s) => {
      if (typeof s.lastLoginHint === "string") setHint(s.lastLoginHint);
    });
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true);
    try {
      await offlineEnter(pw);
    } finally {
      setBusy(false);
      setPw("");
    }
  };

  return (
    <Card title={t("Offline mode — server unreachable")}>
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted">
          {t("Read-only access to your cached items.")}
          {builtAt ? ` ${t("Cached")}: ${new Date(builtAt * 1000).toLocaleString()}.` : ""}
          {hint ? ` ${hint}` : ""}
        </p>
        {terminal ? (
          <>
            <Hint danger>{error}</Hint>
            <p className="text-xs text-muted">{t("No offline data available — connect to the server.")}</p>
          </>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={submit}>
            <Field label={t("Master password")} value={pw} onChange={setPw} type="password" autoFocus />
            {error && <Hint danger>{error}</Hint>}
            <Submit busy={busy} disabled={!pw || busy}>
              {t("Unlock offline")}
            </Submit>
          </form>
        )}
        <button onClick={() => location.reload()} className="text-xs text-muted hover:text-fg">
          {t("Retry connection")}
        </button>
        <button onClick={clearAuth} className="text-xs text-muted hover:text-fg">
          {t("Sign out")}
        </button>
      </div>
    </Card>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="grid h-full place-items-center bg-bg text-muted">{children}</div>;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center bg-bg">
      <div className="w-[380px] rounded-md border border-border bg-panel shadow-xl">
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <div className="grid h-7 w-7 place-items-center rounded bg-accent text-white"><Shield className="h-4 w-4" /></div>
          <div className="font-semibold">{title}</div>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

function EnrollForm() {
  const { enroll, busy, error } = useVault();
  const t = useT();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const tooShort = pw.length > 0 && pw.length < MIN_MASTER_LEN;
  const mismatch = confirm.length > 0 && pw !== confirm;
  const canSubmit = pw.length >= MIN_MASTER_LEN && pw === confirm && !busy;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) enroll(pw);
      }}
    >
      <p className="text-xs text-muted">
        Your master password encrypts your vault keys in this browser. It is never sent to the
        server and cannot be recovered — store it safely.
      </p>
      <Field label={`${t("Master password")} (min ${MIN_MASTER_LEN})`} value={pw} onChange={setPw} type="password" autoFocus />
      <Field label={t("Confirm")} value={confirm} onChange={setConfirm} type="password" />
      {tooShort && <Hint danger>At least {MIN_MASTER_LEN} characters.</Hint>}
      {mismatch && <Hint danger>Passwords do not match.</Hint>}
      {error && <Hint danger>{error}</Hint>}
      <Submit busy={busy} disabled={!canSubmit}>
        {t("Create vault")}
      </Submit>
    </form>
  );
}

function UnlockForm() {
  const { unlock, busy, error, lock } = useVault();
  const t = useT();
  const [pw, setPw] = useState("");
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (pw && !busy) unlock(pw);
      }}
    >
      <Field label={t("Master password")} value={pw} onChange={setPw} type="password" autoFocus />
      {error && <Hint danger>{error}</Hint>}
      <Submit busy={busy} disabled={!pw || busy}>
        {t("Unlock")}
      </Submit>
      <button type="button" onClick={lock} className="text-xs text-muted hover:text-fg">
        {t("Use a different account → sign out")}
      </button>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      <input
        type={type}
        autoFocus={autoFocus}
        className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Hint({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div className={`text-xs ${danger ? "text-danger" : "text-muted"}`}>{children}</div>;
}

function Submit({ children, busy, disabled }: { children: React.ReactNode; busy: boolean; disabled: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="mt-1 rounded bg-accent px-3 py-2 font-medium text-white hover:bg-accent2 disabled:opacity-60"
    >
      {busy ? "…" : children}
    </button>
  );
}
